import * as React from "react";
import { Search } from "lucide-react";
import { Product } from "@/types/product";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import SearchSuggestions from "./SearchSuggestions";
import { ExportButton } from "./ExportButton";
import { SearchStats } from "./SearchStats";
import { performSearch } from "./utils/searchUtils";

interface SearchInputProps {
  onResultsChange: (results: Product[]) => void;
}

const SearchInput = ({ onResultsChange }: SearchInputProps) => {
  const [query, setQuery] = React.useState<string>("");
  const [suggestions, setSuggestions] = React.useState<Product[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [totalHits, setTotalHits] = React.useState<number>(0);
  const searchTimeout = React.useRef<NodeJS.Timeout>();
  const { toast } = useToast();

  const handleSearch = async () => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      onResultsChange([]);
      setTotalHits(0);
      return;
    }

    setIsLoading(true);
    try {
      const { hits, nbHits } = await performSearch(query);
      setSuggestions(hits);
      onResultsChange(hits);
      setTotalHits(nbHits);
    } catch (error) {
      console.error("Search error:", error);
      toast({
        title: "Search Error",
        description: "Failed to fetch search results. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (product: Product) => {
    setQuery(product.title);
    onResultsChange([product]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setIsFocused(false);
      handleSearch();
    }
  };

  React.useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      onResultsChange([]);
      setTotalHits(0);
      return;
    }

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(handleSearch, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [query]);

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search products..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 200)}
            onKeyDown={handleKeyDown}
            className="pl-10 pr-4 h-12 text-lg bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 focus:border-primary focus:ring-primary transition-all duration-200 shadow-sm hover:shadow-md"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
            </div>
          )}
        </div>
        <Button
          onClick={handleSearch}
          className="h-12 px-6 text-lg font-semibold bg-primary hover:bg-primary/90 text-white transition-all duration-200 shadow-sm hover:shadow-md"
        >
          Go!
        </Button>
        <ExportButton suggestions={suggestions} query={query} />
      </div>

      <SearchStats
        query={query}
        totalHits={totalHits}
        suggestions={suggestions}
      />

      <SearchSuggestions
        suggestions={suggestions}
        onSelect={handleSelect}
        show={isFocused}
        searchQuery={query}
      />
    </div>
  );
};

export default SearchInput;